import {
  differenceWith,
  find,
  findIndex,
  isEmpty,
  isEqual,
  keyBy,
  map,
  sortBy,
} from "lodash/fp";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";

import {
  areSpeakersEqual,
  filterTranscriptBySpeakers,
  getSpeakerSortValue,
} from "@app/analytics/MeetingReport/utils";
import type { ReactionHighlight, TranscriptSpeaker } from "@app/common/types";
import * as Arrays from "@app/utils/arrays";

import type { TranscriptItem } from "../types";

export type TextSize = "0.875em" | "1em" | "1.125em";

type ActiveItem = { id: string; type: "paragraph" | "chapter" };

interface TranscriptStateValues {
  /** the item index to scroll to in the transcript */
  activeItemIndex: number | null;
  activeItem: ActiveItem | null;
  allSpeakers: TranscriptSpeaker[];
  /** Internal-only; whether the transcript should auto-scroll to turns as the video is playing */
  autoFollow: boolean;
  /** The subset of speakers whose turns should be displayed */
  filteredSpeakers: TranscriptSpeaker[];
  speakersWithTalkTime: TranscriptSpeaker[];
  reactionsByParagraphId: Map<string, ReactionHighlight[]>;
  reactionsEnabled: boolean;
  transcriptItems: TranscriptItem[];
  filteredTranscriptItems: TranscriptItem[];
  /** Turn paragraph to highlight as the "active" paragraph when the video is playing */
  playbackParagraphId: string | null;
  textSize: TextSize;
  /** Whether the transcript turns are populated and available to the current user */
  transcriptAvailable: boolean;
}

interface TranscriptState extends TranscriptStateValues {
  // actions
  resetAutoFollow: () => void;
  resetState: () => void;
  clearSpeakerFilters: () => void;

  // setters
  setActiveItem: (item: ActiveItem | null, focus?: boolean) => void;
  setAllSpeakers: (speakers: TranscriptSpeaker[]) => void;
  setAutoFollow: (autoFollow: boolean) => void;
  setFilteredSpeakers: (speakers: TranscriptSpeaker[]) => void;
  setSpeakersWithTalkTime: (speakers: TranscriptSpeaker[]) => void;
  toggleReactionsEnabled: () => void;
  setPlaybackParagraphId: (id: string | null) => void;
  getPlaybackParagraphIndex: () => number;
  seekChapter: (nextChapterId: string) => void;
  setReactionsByParagraphId: (
    reactionsByParagraphId: Map<string, ReactionHighlight[]>
  ) => void;
  setTextSize: (textSize: TextSize) => void;
  setTranscriptAvailable: (transcriptAvailable: boolean) => void;
  setTranscriptItems: (transcriptItems: TranscriptItem[]) => void;
}

const DEFAULT_STATE: TranscriptStateValues = {
  activeItem: null,
  activeItemIndex: null,
  allSpeakers: [],
  autoFollow: false,
  filteredSpeakers: [],
  speakersWithTalkTime: [],
  reactionsByParagraphId: new Map<string, ReactionHighlight[]>(),
  reactionsEnabled: true,
  playbackParagraphId: null,
  textSize: "1em",
  transcriptAvailable: false,
  filteredTranscriptItems: [],
  transcriptItems: [],
};

const sortSpeakers = (speakers: TranscriptSpeaker[]) =>
  sortBy((s) => getSpeakerSortValue(s.name), speakers);

const updateFocusedItem = (
  transcriptItems: TranscriptItem[],
  item: ActiveItem | null
) => {
  let focusedItemIndex = -1;
  let focusedItem: TranscriptItem | null = null;
  if (item) {
    focusedItemIndex = findIndex({ id: item.id }, transcriptItems);
    focusedItem = transcriptItems[focusedItemIndex];
  }

  const currentFocusedItemIndex = findIndex({ focused: true }, transcriptItems);
  const currentFocusedItem = transcriptItems[currentFocusedItemIndex];

  let newList = transcriptItems;
  if (currentFocusedItem) {
    newList = Arrays.replaceAt(
      transcriptItems,
      { ...currentFocusedItem, focused: false },
      currentFocusedItemIndex
    );
  }

  if (!focusedItem) return newList;
  return Arrays.replaceAt(
    newList,
    { ...focusedItem, focused: true },
    focusedItemIndex
  );
};

const updateTranscriptItems = (
  state: TranscriptState,
  newTranscriptItems: TranscriptItem[]
) => {
  if (isEmpty(state.transcriptItems)) {
    const excludeSpeakers = differenceWith(
      areSpeakersEqual,
      state.allSpeakers,
      state.filteredSpeakers
    );
    return {
      transcriptItems: newTranscriptItems,
      filteredTranscriptItems: filterTranscriptBySpeakers(
        newTranscriptItems,
        excludeSpeakers
      ),
    };
  }

  const focusedItem = find({ focused: true }, state.transcriptItems);
  if (focusedItem) {
    const focusedItemIndex = findIndex({ focused: true }, newTranscriptItems);
    if (focusedItemIndex !== -1) {
      newTranscriptItems[focusedItemIndex] = { ...focusedItem, focused: true };
    }
  }

  const filteredTranscriptItems = updateFilteredTranscriptItems(
    state,
    newTranscriptItems,
    state.filteredSpeakers
  );

  return {
    transcriptItems: newTranscriptItems,
    filteredTranscriptItems,
  };
};

const mergeSpeakersWithTalkTime = (
  allSpeakers: TranscriptSpeaker[],
  speakersWithTalkTime: TranscriptSpeaker[]
): TranscriptSpeaker[] => {
  const talkTimeSpeakersByName = keyBy("name", speakersWithTalkTime);
  const speakers = isEmpty(allSpeakers) ? speakersWithTalkTime : allSpeakers;

  return map((speaker) => {
    if (!speaker.name) return speaker;
    const talkTimeSpeaker = talkTimeSpeakersByName[speaker.name];
    return talkTimeSpeaker ? { ...talkTimeSpeaker } : speaker;
  }, speakers);
};

const updateFilteredTranscriptItems = (
  state: TranscriptState,
  transcriptItems: TranscriptItem[],
  speakersToInclude: TranscriptSpeaker[]
) => {
  if (isEmpty(speakersToInclude)) {
    return DEFAULT_STATE.transcriptItems;
  }

  const excludeSpeakers = differenceWith(
    areSpeakersEqual,
    state.allSpeakers,
    speakersToInclude
  );
  return filterTranscriptBySpeakers(transcriptItems, excludeSpeakers);
};

const useTranscriptStore = createWithEqualityFn<TranscriptState>(
  (set, get) => ({
    ...DEFAULT_STATE,
    clearSpeakerFilters: () =>
      set((state) => ({
        filteredTranscriptItems: state.transcriptItems,
        filteredSpeakers: sortSpeakers(state.allSpeakers),
      })),
    resetAutoFollow: () => set({ autoFollow: false, activeItemIndex: null }),
    resetState: () =>
      set((state) => ({
        ...DEFAULT_STATE,
        // maintain setting state across meeting reports
        textSize: state.textSize,
        reactionsEnabled: state.reactionsEnabled,
      })),

    setActiveItem: (item, focus = true) => {
      set((state) => {
        let transcriptItems = state.transcriptItems;
        let filteredTranscriptItems = state.filteredTranscriptItems;

        // set the focused item or remove all focused items
        // if the item is null
        if (focus && (!item || item.type === "paragraph")) {
          transcriptItems = updateFocusedItem(state.transcriptItems, item);
          filteredTranscriptItems = updateFocusedItem(
            state.filteredTranscriptItems,
            item
          );
        }

        if (item === null) {
          return {
            activeItemIndex: null,
            activeItem: null,
            transcriptItems,
            filteredTranscriptItems,
          };
        } else {
          const activeItemIndex = findIndex(
            { id: item.id },
            state.filteredTranscriptItems
          );
          if (activeItemIndex === -1) {
            return { activeItemIndex: null, activeItem: null };
          }

          return {
            activeItemIndex,
            activeItem: item,
            transcriptItems,
            filteredTranscriptItems,
          };
        }
      });
    },
    setAllSpeakers: (speakers) =>
      set((state) => {
        // sort speakers when adding to store to help memoization in components
        const allSpeakers = sortSpeakers(
          mergeSpeakersWithTalkTime(speakers, state.speakersWithTalkTime)
        );
        return {
          allSpeakers,
          filteredSpeakers: allSpeakers,
        };
      }),
    setAutoFollow: (autoFollow) => set({ autoFollow }),
    setFilteredSpeakers: (filteredSpeakers) =>
      set((state) => {
        const filteredTranscriptItems = updateFilteredTranscriptItems(
          state,
          state.transcriptItems,
          filteredSpeakers
        );
        return {
          filteredTranscriptItems,
          filteredSpeakers: sortSpeakers(filteredSpeakers),
        };
      }),
    setSpeakersWithTalkTime: (speakersWithTalkTime) => {
      set((state) => {
        const allSpeakers = sortSpeakers(
          mergeSpeakersWithTalkTime(state.allSpeakers, speakersWithTalkTime)
        );
        return {
          allSpeakers,
          filteredSpeakers: isEmpty(state.filteredSpeakers)
            ? sortSpeakers(allSpeakers)
            : state.filteredSpeakers,
          speakersWithTalkTime,
        };
      });
    },
    getPlaybackParagraphIndex: () => {
      const id = get().playbackParagraphId;
      if (!id) return -1;
      return findIndex({ id }, get().transcriptItems);
    },
    seekChapter: (nextChapterId) => {
      const nextChapterIndex = findIndex(
        { id: nextChapterId },
        get().filteredTranscriptItems
      );

      // ensure the chapter exists
      if (nextChapterIndex === -1) return;
      get().setActiveItem({ id: nextChapterId, type: "chapter" });
    },
    setReactionsByParagraphId: (reactionsByParagraphId) =>
      set({ reactionsByParagraphId }),
    setPlaybackParagraphId: (playbackParagraphId) => {
      set((state) => {
        if (
          state.activeItem != null &&
          state.playbackParagraphId === playbackParagraphId
        ) {
          return state;
        }

        const activeItem: ActiveItem | null = playbackParagraphId
          ? { id: playbackParagraphId, type: "paragraph" }
          : null;

        if (state.autoFollow) {
          state.setActiveItem(activeItem, false);
        }

        return {
          playbackParagraphId,
          transcriptItems: updateFocusedItem(state.transcriptItems, activeItem),
          filteredTranscriptItems: updateFocusedItem(
            state.filteredTranscriptItems,
            activeItem
          ),
        };
      });
    },
    setTextSize: (textSize) => set(() => ({ textSize })),
    setTranscriptAvailable: (transcriptAvailable) =>
      set({ transcriptAvailable }),
    toggleReactionsEnabled: () =>
      set((state) => ({ reactionsEnabled: !state.reactionsEnabled })),
    setTranscriptItems: (transcriptItems) => {
      if (isEqual(get().transcriptItems, transcriptItems)) return;
      set((state) => ({ ...updateTranscriptItems(state, transcriptItems) }));
    },
  }),
  shallow
);

export default useTranscriptStore;
